import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail, UserPlus } from "lucide-react";
import { toast } from "sonner";

const roles = [{ value: "volunteer", label: "Vrijwilliger" }, { value: "planner", label: "Planner" }, { value: "admin", label: "Beheerder" }];

export function InviteDialog({ onUpdated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "volunteer" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pendingId, setPendingId] = useState(null);
  const start = () => {
    setForm({ name: "", email: "", role: "volunteer" }); setError(""); setPendingId(null); setOpen(true);
  };
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      if (pendingId) await api.post(`/admin/users/${pendingId}/invite`);
      else await api.post("/admin/users/invite", { ...form, name: form.name.trim(), email: form.email.trim() });
      toast.success("Uitnodiging per e-mail verstuurd"); setOpen(false); onUpdated();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(formatApiError(detail));
      if (detail?.user_id) setPendingId(detail.user_id);
      onUpdated();
    } finally { setBusy(false); }
  };
  return <>
    <Button variant="outline" onClick={start} data-testid="invite-user-btn"><Mail className="w-4 h-4 mr-1.5" /> Uitnodigen via mail</Button>
    <Dialog open={open} onOpenChange={(value) => !busy && setOpen(value)}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" /> Medewerker uitnodigen</DialogTitle>
          <DialogDescription>De medewerker ontvangt een link om zelf een wachtwoord te kiezen. De link is 7 dagen geldig.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
          {error && <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="invite-error">{error}</p>}
          <div className="space-y-1.5"><Label htmlFor="invite-name">Naam</Label><Input id="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={100} disabled={busy || !!pendingId} data-testid="invite-name" /></div>
          <div className="space-y-1.5"><Label htmlFor="invite-email">E-mail</Label><Input id="invite-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={busy || !!pendingId} data-testid="invite-email" /></div>
          {user.role === "admin" ? <div className="space-y-1.5">
            <Label htmlFor="invite-role">Rol</Label>
            <Select value={form.role} onValueChange={(role) => setForm({ ...form, role })} disabled={busy || !!pendingId}>
              <SelectTrigger id="invite-role" data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((role) => <SelectItem key={role.value} value={role.value} data-testid={`invite-role-${role.value}`}>{role.label}</SelectItem>)}</SelectContent>
            </Select>
          </div> : <p className="text-sm text-muted-foreground" data-testid="invite-planner-role">Je nodigt deze medewerker uit als vrijwilliger.</p>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy} data-testid="invite-cancel">Annuleren</Button>
            <Button type="submit" disabled={busy || !form.name.trim() || !form.email.trim()} data-testid="invite-submit">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{pendingId ? "Opnieuw versturen" : "Uitnodiging versturen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </>;
}

export function InvitationStatus({ employee }) {
  const text = !employee.active ? "Inactief" : employee.invited
    ? (employee.invitation_status === "failed" ? "Versturen mislukt" : "Wacht op activatie") : "Actief";
  return <span data-testid={`invitation-status-${employee.id}`} className={`text-xs font-semibold px-2 py-1 rounded-full ${employee.invited ? "bg-amber-100 text-amber-800" : employee.active ? "bg-red-100 text-red-700" : "bg-rose-100 text-rose-700"}`}>{text}</span>;
}

export function ResendInviteButton({ employee, onUpdated }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!employee.invited || !employee.active || (user.role !== "admin" && employee.role !== "volunteer")) return null;
  const resend = async () => {
    setBusy(true);
    try { await api.post(`/admin/users/${employee.id}/invite`); toast.success("Nieuwe uitnodiging verstuurd; de vorige link is vervallen"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); onUpdated(); }
  };
  return <Button variant="ghost" size="sm" disabled={busy} onClick={resend} data-testid={`resend-invite-${employee.id}`}>
    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />}Opnieuw versturen
  </Button>;
}
